"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shadcn/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shadcn/ui/sheet";
import {
  UserEvents,
  getMixpanelDistinctId,
  trackEvent,
} from "@punchup/analytics";
import {
  convertToAffiliate,
  isTicketmasterUrl,
} from "@/utils/ticketmasterConverter";
import { useCallback, useState } from "react";

import Link from "next/link";
import { ResponsiveShowRow } from "@/components/shows";
import { ShowInfo } from "@/data/shows";
import { WaitlistButton } from "@punchup/venues/client";
import { Zap } from "lucide-react";
import { cn } from "@/utils/cn";
import { useGate } from "statsig-react";
import { useMediaQuery } from "../common/hooks/useMediaQuery";
import { useShowRowContext } from "@/components/shows/ShowRowContext";

export function ShowRowBuyButton() {
  const {
    shows,
    trackBuyTickets,
    handleBuyTicketsClick,
    isTixologi,
    comedian,
    location,
    isNearby,
    internalReferrer,
    pageSection,
    ctaSource,
    waitlistSubscription,
  } = useShowRowContext();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { value: ticketmasterAffiliateEnabled } = useGate(
    "ticketmaster_affiliate_converter"
  );

  const multipleShows = shows.length > 1;

  const handleChooseDatesClick = useCallback(() => {
    // Check if this is a Ticketmaster show group
    const isTicketmasterEvent =
      shows[0]?.id.startsWith("tm-") || comedian.id.startsWith("tm-comedian-");

    trackEvent(
      UserEvents.BUY_TICKETS_CLICKED,
      {
        ComedianId: isTicketmasterEvent ? null : comedian.id,
        ComedianName: comedian.display_name,
        Source: location,
        IsNearby: isNearby,
        IsShowGroup: true,
        ShowCount: shows.length,
        ...(internalReferrer && { InternalReferrer: internalReferrer }),
        ...(pageSection && ctaSource
          ? {
              cta_source: ctaSource,
              page_section: pageSection,
            }
          : {}),
      },
      { send_immediately: true }
    );

    if (isDesktop) {
      setIsDialogOpen(true);
    } else {
      setIsSheetOpen(true);
    }
  }, [
    shows,
    comedian.id,
    comedian.display_name,
    location,
    internalReferrer,
    isNearby,
    isDesktop,
    pageSection,
    ctaSource,
  ]);

  const allShowsSoldOut = shows.every((show) => show.soldOut);

  const buttonClassName =
    "bg-primary rounded-lg text-white font-semibold text-[14px] font-semibold leading-[26px] flex flex-row items-center justify-center py-[7px] px-2 w-full";

  // if we have multiple shows, we need to display a button to open the modal/drawer
  if (multipleShows) {
    if (isDesktop) {
      return (
        <div className="flex flex-col gap-2 w-full">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleChooseDatesClick();
                }} // TODO: handle VIP shows
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                }}
                className={buttonClassName}
              >
                {allShowsSoldOut ? "Sold Out!" : "Choose Dates"}
              </button>
            </DialogTrigger>
            <DialogContent className="p-0 max-w-[1200px] w-[90%] max-h-[90vh] flex flex-col overflow-hidden rounded-xl border-none">
              <DialogHeader className="p-4 border-b mb-0">
                <DialogTitle className="text-xl">Choose a Date</DialogTitle>
              </DialogHeader>
              <div
                className="p-4 w-full overflow-auto"
                style={{ maxHeight: "calc(90vh - 65px)" }}
              >
                <ShowList
                  sortedShows={shows}
                  handleIndividualShowClick={() => handleBuyTicketsClick({})} // TODO: handle VIP shows
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      );
    }

    return (
      <div className="w-full">
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleChooseDatesClick();
              }} // TODO: handle VIP shows
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
              }}
              className={buttonClassName}
            >
              Choose Dates
            </button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            className="p-0 pb-safe max-h-[92vh] h-[92vh] overflow-hidden rounded-t-xl"
          >
            <SheetHeader className="p-4 border-b mb-0 text-left">
              <SheetTitle className="text-xl">Choose a Date</SheetTitle>
            </SheetHeader>
            <div
              className="p-4 w-full overflow-auto"
              style={{ height: "calc(92vh - 65px)" }}
            >
              <ShowList
                sortedShows={shows}
                handleIndividualShowClick={() => handleBuyTicketsClick({})}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  // otherwise, we have a single show so we can display the "buy" button directly
  const show = shows[0];

  // Convert the main ticket link if it's Ticketmaster. The anchor below handles
  // navigation via `target="_blank"`, so the affiliate conversion must live on
  // the href (not inside a window.open handler) to avoid opening a second window.
  const ticketLink =
    show.ticketLink &&
    ticketmasterAffiliateEnabled &&
    isTicketmasterUrl(show.ticketLink)
      ? convertToAffiliate(show.ticketLink, {
          src: location,
          distinctId: getMixpanelDistinctId(),
        })
      : show.ticketLink;

  // Convert VIP ticket link if it's Ticketmaster
  const vipTicketLink =
    show.vipTicketLink &&
    ticketmasterAffiliateEnabled &&
    isTicketmasterUrl(show.vipTicketLink)
      ? convertToAffiliate(show.vipTicketLink, {
          src: location,
          distinctId: getMixpanelDistinctId(),
        })
      : show.vipTicketLink;

  return (
    <div className="flex flex-col-reverse md:flex-col gap-2 w-full">
      {show.soldOut ? (
        <WaitlistButton
          show={{
            id: show.id,
            comedian_id: show.comedian_id,
            created_at: "",
            datetime: show.datetimeOriginal,
            flags: show.flags,
            is_sold_out: show.soldOut,
            location: show.location,
            metadata_text: show.metadataText,
            poster_img: show.posterImg,
            presale_code: show.presaleCode,
            ticket_link: show.ticketLink,
            tixologi_event_id: show.tixologiEventId,
            title: show.title || null,
            venue: show.venue,
            venue_id: show.venueId,
            vip_ticket_link: show.vipTicketLink,
            published_at: null,
            comedian: comedian,
            show_comedians: [
              {
                ...comedian,
                is_punchup_verified: comedian.is_punchup_verified || false,
                ordering: 0,
                hidden_from_comedian_page: false,
              },
            ],
            venue_pages: [],
          }}
          ownerId={comedian.id}
          isVenue={false}
          className={buttonClassName}
          comedianSlug={comedian.slug}
          injectedSubscription={waitlistSubscription}
        />
      ) : (
        <>
          {isTixologi ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleBuyTicketsClick({});
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
              }}
              className={buttonClassName}
            >
              <p className="flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Fast Checkout
              </p>
            </button>
          ) : ticketLink ? (
            <Link
              href={ticketLink}
              target="_blank"
              // The anchor navigates via target="_blank"; only track here.
              // Calling handleBuyTicketsClick would window.open a second
              // (popup) window on top of the anchor's navigation.
              onClick={(e) => {
                e.stopPropagation();
                trackBuyTickets({});
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
              }}
              className={buttonClassName}
            >
              Buy Tickets
            </Link>
          ) : null}

          {!!vipTicketLink && (
            <Link
              href={vipTicketLink}
              target="_blank"
              // Track only — the anchor handles navigation (see above).
              onClick={(e) => {
                e.stopPropagation();
                trackBuyTickets({ isVIPShow: true });
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
              }}
              className={cn(buttonClassName, "bg-slate-gray-600 text-white")}
            >
              Buy VIP Tickets
            </Link>
          )}
        </>
      )}
    </div>
  );
}

interface ShowListProps {
  sortedShows: ShowInfo[];
  handleIndividualShowClick: (show: ShowInfo) => void;
}

const ShowList = ({
  sortedShows,
  handleIndividualShowClick,
}: ShowListProps) => {
  const { comedian, location, isNearby, internalReferrer } =
    useShowRowContext();

  return (
    <div className="w-full">
      <div className="grid gap-4 w-full">
        {sortedShows.map((show) => (
          <div key={show.id} className="w-full">
            <ResponsiveShowRow
              shows={[show]}
              comedian={comedian}
              location={location}
              isNearby={isNearby}
              internalReferrer={internalReferrer}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
